import React from 'react';
import { Stack, Box, Text } from '@chakra-ui/react';
import PlanColumn, { PlanColumnProps } from './PlanColumn';
import { TabType } from './TabNav';

const PLANS_BY_TAB = {
  'Pay As You Go': {
    sectionDescription:
      'Think of it as our a la carte menu. Pay only when you use HOVER, whether it’s every so often or just one time.',
    plans: [
      {
        name: 'Pay As You Go',
        description:
          'If you prefer the traditional approach of paying per use.',
        price: 2500,
        jobDiscount: '10',
        ctaButton: {
          label: 'First Job Free',
          href: './signup/',
        },
        features: [
          {
            name: 'Measurement PDF',
            tooltip:
              'Per property measurements are accurate to within 5% (in aggregate).',
          },
          {
            name: 'Interactive 3D Model',
            tooltip:
              'Help homeowners understand their estimates and see their new roof, siding, and windows with real products on their homes.',
          },
          {
            name: '12-Month Property Storage',
            tooltip:
              'Keep all your projects, measurements, and 3D models in our cloud for 12 months.',
          },
          {
            name: 'Basic Data Export',
            tooltip: 'Export all measurement data to XLS and PDF.',
          },
        ],
      },
    ],
  },
  Membership: {
    sectionDescription:
      'Become a member to unlock discounts on every property and integrate with your favorite tools.',
    plans: [
      {
        name: 'Measure',
        description:
          'If you prefer a self-service approach that gives you access to integrated workflows.',
        price: 2500,
        jobDiscount: '10',
        ctaButton: {
          label: 'Get Started',
          href: './signup/',
        },
        features: [
          {
            name: 'Measurement PDF',
            tooltip:
              'Per property measurements are accurate to within 5% (in aggregate).',
          },
          {
            name: 'Custom Branding',
            tooltip: 'Add your logo to all measurement PDFs.',
          },
          {
            name: 'Interactive 3D Model',
            tooltip:
              'Help homeowners understand their estimates and see their new roof, siding, and windows with real products on their homes.',
          },
          {
            name: 'Unlimited Property Storage',
            tooltip:
              'Keep all your projects, measurements, and 3D models in our cloud without time limits.',
          },
          {
            name: 'Workflow Integrations',
            tooltip:
              'Eliminate manual data entry by integrating seamlessly with JobNimbus, JobProgress, Acculynx, CoreLogic, and more.',
          },
          {
            name: 'Standard Data Export',
            tooltip: 'Export all measurement data to XLS, JSON, XML, and PDF.',
          },
          {
            name: 'Remote Engagement',
            tooltip:
              'Invite anyone to photograph a property for you using HOVER Connect, for which you will receive full measurements and a 3D model.',
          },
          {
            name: 'Unlimited Users',
            tooltip:
              "One subscription per org, unlimited users. It's really that simple.",
          },
        ],
      },
      {
        name: 'Scale',
        description:
          'If you prefer a more personalized experience that accommodates larger job volumes.',
        price: 5000,
        jobDiscount: '15+',
        ctaButton: {
          label: 'Contact Sales',
          href: 'https://calendly.com/hover-sdr/hover-membership-website',
        },
        includesText: 'includes everything in Measure plus...',
        features: [
          {
            name: 'Advanced Data Export',
            tooltip:
              'Export all measurement data to XLS, JSON, XML and PDF and all 3D model data to popular CAD types: SKP, DXF, DWG, .esx.',
          },
          {
            name: 'Single Sign On (SSO)',
            tooltip:
              'Enable enterprise-grade security and authentication for your entire team.',
          },
          {
            name: 'Advanced User Permissions',
            tooltip:
              'Control access to certain features for third parties and internal team members.',
          },
          {
            name: 'Assigned Account Managers',
            tooltip:
              'Talk to an assigned Account Manager for questions about the product or your business.',
          },
          {
            name: 'Priority Support',
            tooltip:
              'Jump to the front of the line with priority support queues.',
          },
          {
            name: 'Priority Job Processing',
            tooltip:
              'Unlock quicker average delivery without paying for expedited orders.',
          },
          {
            name: 'User Training and Education',
            tooltip:
              'Get started off on the right foot with customized training and education.',
          },
        ],
        isRecommended: true,
      },
      {
        name: 'Transform',
        description:
          'If you’re looking to power your business from sales to production.',
        price: 5000,
        jobDiscount: '15+',
        ctaButton: {
          label: 'Contact Sales',
          href: 'https://calendly.com/hover-sdr/hover-membership-website',
        },
        includesText: 'includes everything in Scale plus...',
        features: [
          {
            name: 'Accurate Project Estimation',
            tooltip:
              'Accurate estimates for multiple trades in seconds that reflect job complexity and your unique pricing.',
          },
          {
            name: 'Simplified Proposals & Contracts',
            tooltip:
              'Automatically populate your contract templates and get digital signatures while onsite.',
          },
          {
            name: 'Centralized Production Console',
            tooltip:
              'Demystify and speed up project management with all the dependable resources you need to produce a job, from detailed takeoffs to measurements and job economics.',
          },
          {
            name: 'Supplier-Direct Ordering',
            tooltip:
              'Order electronically from leading suppliers with zero manual entry and without leaving the console.',
          },
          {
            name: 'Materials Ordering',
            tooltip:
              'Precise, pre-populated, material lists for multiple trades.',
          },
          {
            name: 'Labor Management',
            tooltip:
              'Generate work orders in a click that reflect a project’s specific installation needs.',
          },
          {
            name: 'White-glove Onboarding',
            tooltip:
              'White glove service to customize HOVER features to reflect your pricing and process in weeks, not months.',
          },
        ],
        isCustom: true,
      },
    ],
  },
};

const PlanColumns = ({
  selectedTab = 'Membership',
}: {
  selectedTab: TabType;
}) => {
  const { sectionDescription, plans } = PLANS_BY_TAB[selectedTab];

  return (
    <Box maxW="7xl" mx="auto" px="5">
      <Text fontSize="2xl" mb="32" textAlign="center" maxW="2xl" mx="auto">
        {sectionDescription}
      </Text>

      <Stack
        direction={['column', 'column', 'row']}
        spacing={{ base: '20', md: '3', lg: '8' }}
      >
        {plans.map(({ name, ...rest }: PlanColumnProps) => (
          <PlanColumn key={name} name={name} {...rest} />
        ))}
      </Stack>
    </Box>
  );
};

export default PlanColumns;
